'use strict'

const uuid = require('uuid')
const ky = require('ky/umd').default
const { safeGet } = require('./safeGet')
const {
  wixCodeLogLevel,
  siteMonitoringSeverity,
  convertToSiteMonitoringSeverity
} = require('./wixCodeLogLevel')
const { create: createConfigurationService } = require('./configurationService')

const UNKNOWN_ERROR = '[UNKNOWN ERROR]'
const SCRIPT_ERROR = 'Script error.'

const createPagePath = wixSdk => {
  const url = wixSdk.location.url.replace(wixSdk.location.baseUrl, '')

  const withoutQueryParameters =
    url.indexOf('?') === -1 ? url : url.slice(0, url.indexOf('?'))

  return withoutQueryParameters || '/'
}

async function sendLogToServer({ appLogger, configurationPromise, log }) {
  const configuration = await configurationPromise
  if (configuration.hasSinks) {
    ky
      .post('https://frog.wix.com/wct', { json: log })
      .catch(e => appLogger.warn(e))
  }
}

function sendLog({
  appLogger,
  wixSdk,
  configurationPromise,
  ignoredConsoleMessages,
  metaSiteId,
  message = UNKNOWN_ERROR,
  severity = siteMonitoringSeverity.DEFAULT,
  sourceLocation = {}
}) {
  if (message === SCRIPT_ERROR || ignoredConsoleMessages.includes(message)) {
    return
  }

  const log = {
    logName: '',
    timestamp: new Date().toISOString(),
    severity,
    labels: {
      siteUrl: safeGet(() => wixSdk.location.baseUrl, ''),
      namespace: 'Corvid',
      tenantId: metaSiteId,
      viewMode: safeGet(() => wixSdk.window.viewMode, ''),
      revision: safeGet(() => wixSdk.site.revision.toString(), '')
    },
    operation: {
      id: uuid.v4(),
      producer: safeGet(() => createPagePath(wixSdk), ''),
      first: false,
      last: false
    },
    sourceLocation,
    jsonPayload: {
      message
    }
  }

  sendLogToServer({ appLogger, configurationPromise, log })
}

function getMessageFromErrorObject(error) {
  return error.stack || error.message || error.name || error
}

function createNiceStackTrace(message, stack) {
  try {
    return (
      message +
      '\n' +
      stack
        .split('\n')
        .slice(1)
        .join('\n')
    )
  } catch (e) {
    return message + '\n' + stack
  }
}

function registerToConsoleMessages(callback, onWorkerLoggerLog) {
  return onWorkerLoggerLog(({ logLevel, args, stack }) => {
    const joinedArgs = args.join(' ')
    if (logLevel === wixCodeLogLevel.ERROR) {
      const message = createNiceStackTrace(joinedArgs, stack)
      callback({ message, severity: siteMonitoringSeverity.ERROR })
    } else if (logLevel !== wixCodeLogLevel.VERBOSE) {
      const severity = convertToSiteMonitoringSeverity(logLevel)
      callback({ message: joinedArgs, severity })
    }
  })
}

function registerToUncaughtRejections(callback) {
  const listener = event => {
    const error = event.reason || {}
    const message = getMessageFromErrorObject(error)
    callback({ message, severity: siteMonitoringSeverity.ERROR })
  }

  self.addEventListener('unhandledrejection', listener)

  return () => self.removeEventListener('unhandledrejection', listener)
}

const registerListeners = ({ sendLog, onWorkerLoggerLog }) => {
  const unregisterUncaughtRejections = registerToUncaughtRejections(sendLog)
  const unregisterConsoleErrors = registerToConsoleMessages(
    sendLog,
    onWorkerLoggerLog
  )

  return () => {
    unregisterUncaughtRejections()
    unregisterConsoleErrors()
  }
}

const getConfigurationBaseUrl = wixSdk => {
  const viewMode = wixSdk.window.viewMode
  // when we are in an iframe instead of web worker for SEO flows
  // and headless browsers, we should be relative to the iframe url,
  // which looks like https://<guid>.pub.wix-code.com
  if (viewMode === 'Preview' || typeof window !== 'undefined') {
    return ''
  }

  return wixSdk.location.baseUrl
}

const init = ({
  appLogger,
  wixSdk,
  instance,
  metaSiteId,
  onLog: onWorkerLoggerLog,
  ignoredConsoleMessages
}) => {
  const configurationService = createConfigurationService({
    appLogger,
    baseUrl: getConfigurationBaseUrl(wixSdk),
    metaSiteId,
    instance
  })

  const configurationPromise = configurationService.fetchConfiguration()

  const _sendLog = ({ message, severity, sourceLocation }) =>
    sendLog({
      appLogger,
      wixSdk,
      configurationPromise,
      ignoredConsoleMessages,
      metaSiteId,
      message,
      severity,
      sourceLocation
    })

  const unregisterListeners = registerListeners({
    sendLog: _sendLog,
    onWorkerLoggerLog
  })

  configurationPromise.then(configuration => {
    if (!configuration.hasSinks) {
      unregisterListeners()
    }
  })
}

module.exports = {
  init
}
